import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setAuthToken } from "@/lib/api";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);       // null = checking, false = anonymous
  const [status, setStatus] = useState(null);   // {users_exist, needs_setup, allow_signups}

  const loadStatus = useCallback(async () => {
    try { setStatus(await api.authStatus()); } catch (e) { setStatus({ needs_setup: false, allow_signups: false }); }
  }, []);

  useEffect(() => {
    (async () => {
      await loadStatus();
      const token = localStorage.getItem("df_token");
      if (token) {
        setAuthToken(token);
        try {
          const d = await api.me();
          setUser(d.user);
          return;
        } catch (e) { /* fall through to refresh */ }
      }
      try {
        const d = await api.refresh();
        setAuthToken(d.token);
        localStorage.setItem("df_token", d.token);
        setUser(d.user);
      } catch (e) {
        setAuthToken(null);
        localStorage.removeItem("df_token");
        setUser(false);
      }
    })();
  }, [loadStatus]);

  const finish = (data) => {
    setAuthToken(data.token);
    localStorage.setItem("df_token", data.token);
    setUser(data.user);
  };

  const login = async (email, password) => finish(await api.login({ email, password }));
  const register = async (email, password, name, inviteToken) =>
    finish(await api.register({ email, password, name, invite_token: inviteToken || "" }));
  const resetPassword = async (token, password) => finish(await api.resetPassword({ token, password }));

  const logout = async () => {
    try { await api.logout(); } catch (e) { /* noop */ }
    setAuthToken(null);
    localStorage.removeItem("df_token");
    setUser(false);
    loadStatus();
  };

  return (
    <AuthContext.Provider value={{ user, status, login, register, resetPassword, logout, refreshStatus: loadStatus }}>
      {children}
    </AuthContext.Provider>
  );
}
